import React, { useEffect, useRef } from 'react';

const ParticleNetwork: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    
    // Performance optimization: Adaptive particle count based on screen width
    // Mobile devices get significantly fewer particles to reduce main-thread load
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 30 : 80;
    const connectionDistance = isMobile ? 120 : 180;
    const mouseRadius = 150;

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      baseVx: number;
      baseVy: number;
      size: number;
      opacity: number;
      pulse: number;

      constructor(width: number, height: number) {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        
        // Varying speeds: some slow, some faster
        const speedMultiplier = Math.random() * 0.5 + 0.2;
        const angle = Math.random() * Math.PI * 2;
        this.vx = Math.cos(angle) * speedMultiplier;
        this.vy = Math.sin(angle) * speedMultiplier;
        this.baseVx = this.vx;
        this.baseVy = this.vy;
        
        this.size = Math.random() * 1.5 + 0.5;
        this.opacity = Math.random() * 0.5 + 0.2;
        this.pulse = Math.random() * Math.PI;
      }

      update(width: number, height: number, mouse: { x: number; y: number }) {
        // Subtle drift movement
        this.pulse += 0.01;
        const driftX = Math.sin(this.pulse) * 0.1;
        const driftY = Math.cos(this.pulse) * 0.1;

        // Mouse interaction: slight repulsion
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        let forceX = 0;
        let forceY = 0;
        
        if (dist < mouseRadius) {
          const force = (mouseRadius - dist) / mouseRadius;
          forceX = (dx / dist) * force * -0.5;
          forceY = (dy / dist) * force * -0.5;
        }

        this.x += this.vx + driftX + forceX;
        this.y += this.vy + driftY + forceY;

        // Bounce with friction
        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;
      }

      draw(context: CanvasRenderingContext2D) {
        const currentOpacity = this.opacity * (0.7 + Math.sin(this.pulse) * 0.3);
        context.beginPath();
        context.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        context.fillStyle = `rgba(168, 85, 247, ${currentOpacity})`;
        
        // Disable shadowBlur on mobile for performance
        if (!isMobile) {
            context.shadowBlur = 8;
            context.shadowColor = '#a855f7';
        }
        
        context.fill();
        context.shadowBlur = 0; // Reset for performance
      }
    }

    const init = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Re-evaluate mobile status on resize
      const currentIsMobile = window.innerWidth < 768;
      const currentParticleCount = currentIsMobile ? 30 : 80;

      particles = [];
      for (let i = 0; i < currentParticleCount; i++) {
        particles.push(new Particle(canvas.width, canvas.height));
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p, i) => {
        p.update(canvas.width, canvas.height, mouseRef.current);
        p.draw(ctx);

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDistance) {
            const opacity = (1 - dist / connectionDistance) * 0.25;
            
            // Draw glow part (thicker, lower opacity) - Skip on mobile for performance
            if (!isMobile) {
                ctx.beginPath();
                ctx.strokeStyle = `rgba(168, 85, 247, ${opacity * 0.4})`;
                ctx.lineWidth = 2.5;
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
            }

            // Draw core part (thinner, higher opacity)
            ctx.beginPath();
            ctx.strokeStyle = `rgba(217, 70, 239, ${opacity})`;
            ctx.lineWidth = 0.8;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    window.addEventListener('resize', init);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    
    init();
    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', init);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-0"
    />
  );
};

export default ParticleNetwork;